import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar,
  Chip,
  Stack,
  Paper,
  Divider,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import { useGetTaskCategoryByIdQuery } from '../../store/api';
import { toast } from 'react-toastify';
import { buildTaskCategoryRelationParams } from './taskCategoryApiHelpers';

const DetailItem = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.light',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {label}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 0.5,
              fontWeight: 500,
              wordBreak: 'break-word',
            }}
          >
            {value ?? '—'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  </Grid>
);

const ViewTaskCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetTaskCategoryByIdQuery(
    { id, params: buildTaskCategoryRelationParams() },
    { skip: !id }
  );

  const item = data?.data ?? data ?? null;

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || 'Failed to load task category');
      navigate('/task-categories');
    }
  }, [isError, error, navigate]);

  if (loading || !item) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const statusName =
    item.status?.name ??
    item.status?.label ??
    'Unknown';

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/task-categories')}
            variant="outlined"
          >
            Back
          </Button>

          <Can permission="task-category-edit">
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() =>
                navigate(`/task-categories/edit/${id}`)
              }
            >
              Edit Category
            </Button>
          </Can>
        </Box>

        {/* Main Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 4,
              background:
                'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              {/* Category Avatar */}
              <Avatar
                variant="rounded"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: 4,
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontSize: 34,
                  fontWeight: 700,
                }}
              >
                {item.name?.charAt(0)?.toUpperCase() || 'C'}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  {item.name ?? 'Task Category'}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={statusName}
                    color={
                      statusName.toLowerCase() === 'active'
                        ? 'success'
                        : 'default'
                    }
                  />

                  {item.parent?.name && (
                    <Chip
                      icon={<AccountTreeOutlinedIcon />}
                      label={item.parent?.name}
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Details */}
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
              }}
            >
              Category Information
            </Typography>

            <Grid container spacing={2.5}>
              <DetailItem
                icon={<CategoryOutlinedIcon fontSize="small" />}
                label="Category Name"
                value={item.name}
              />

              <DetailItem
                icon={<AccountTreeOutlinedIcon fontSize="small" />}
                label="Parent Category"
                value={item.parent?.name}
              />

              <DetailItem
                icon={<PersonOutlineIcon fontSize="small" />}
                label="Author"
                value={
                  item.author?.name ??
                  item.author?.username
                }
              />

              <DetailItem
                icon={<AccessTimeIcon fontSize="small" />}
                label="Created At"
                value={item.created_at}
              />

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <NotesOutlinedIcon fontSize="small" />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Description
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.8,
                          color: 'text.primary',
                        }}
                      >
                        {item.description ||
                          'No description available.'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewTaskCategory;